<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.download_grades'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>

                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                    <SelectWithPreferences
                        required
                        class="sm:col-span-4"
                        :options="options.groups"
                        name="group_id"
                        :label="$tc('messages.groups', 1)"
                        property-name="text"
                        :preferences="meta.preferences.group_ids"
                        :default-option="form.group_id"
                        :autoselect="false"
                        property-value="value"
                        v-model="form.group_id"
                    />

                    <SelectWithPreferences
                        required
                        class="sm:col-span-4"
                        :options="options.categories"
                        name="subject_id"
                        :label="$tc('messages.categories', 1)"
                        property-name="text"
                        :preferences="meta.preferences.category_ids"
                        :default-option="form.category_id"
                        :autoselect="false"
                        property-value="value"
                        v-model="form.category_id"
                    />

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.trimester')" for="trimester" />
                            <FormSelect
                                id="trimester"
                                v-model="form.trimester"
                                :options="options.trimesters"
                                class="mt-1 w-full"
                                required
                            />
                            <FormError :value="form.errors.trimester" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $t('messages.percentage') }}
                    </div>
                    <div class="text-gray-600 text-xs">
                        {{ meta.info.percent_explanation }}
                    </div>
                </template>

                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.percentage')" for="percentage" />
                            <FormInput
                                id="percentage"
                                v-model="form.percentage"
                                class="mt-1 w-full"
                                required
                                min="0"
                                max="1000"
                                step="1"
                            />
                            <FormError :value="form.errors.percentage" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-8">
                        <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                        <div class="mt-1 flex gap-x-2 justify-end items-center">
                            <SubmitFormButton :button-text="$t('messages.show')" :processing="form.processing" />

                            <AppButton element="a" :href="downloadUrl">
                                {{ $t('messages.download') }}
                            </AppButton>

                            <DropdownButton v-if="meta.links.gradebook.length">
                                <template v-slot:text>
                                    {{ $t('messages.gradebook') }}
                                </template>
                                <template v-slot:content>
                                    <InertiaLink
                                        v-for="link in meta.links.gradebook"
                                        :href="link.url"
                                        :key="link.url"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ link.text }}
                                    </InertiaLink>
                                </template>
                            </DropdownButton>

                            <AppButton element="InertiaLink" :href="meta.links.activity_dimensions_weight">
                                {{ $t('messages.weight') }}
                            </AppButton>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>
        </form>

        <div v-if="gradesInfo === null" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div v-else>
            <DimensionGradesTable :dimensions="dimensions" :students="gradesInfo" />
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import EmptyState from '@/components/EmptyState.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import DimensionGradesTable from '@/components/pageSpecific/DimensionGradesTable.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import DropdownButton from '@/components/DropdownButton.vue';
import AppButton from '@/components/AppButton.vue';
import SelectWithPreferences from '@/components/SelectWithPreferences.vue';

export default {
    components: {
        SelectWithPreferences,
        AppButton,
        DropdownButton,
        FormInput,
        TitleFieldsFormSection,
        DimensionGradesTable,
        SubmitFormButton,
        PanelPage,
        FormLabel,
        FormError,
        FormSelect,
        EmptyState,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'filters', 'options', 'dimensions', 'gradesInfo', 'categories', 'barChartOptions', 'radarCharts'],
    data() {
        return {
            form: this.$inertia.form({
                group_id: this.filters.group_id,
                category_id: this.filters.category_id,
                trimester: this.filters.trimester,
                percentage: this.filters.percentage,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
    computed: {
        downloadUrl: function () {
            let url = new URL(this.meta.links.index);
            url.searchParams.append('group_id', this.form.group_id);
            url.searchParams.append('category_id', this.form.category_id);
            url.searchParams.append('trimester', this.form.trimester);
            url.searchParams.append('percentage', this.form.percentage);
            url.searchParams.append('download', '1');

            return url.href;
        },
    },
};
</script>
